import type { CalculationResult } from '~/pages/IncomeCalculator/types';

import { FORMULA1120 } from '../../../constants';
import { FormTypes } from '../../../enums';
import lang from '../language.json';
import BreakdownValue from '../styles/BreakdownValue';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';

const {
  FIELDS: { ['1120']: FIELDS },
} = lang;

type ValueProps = {
  monthlyIncome?: number;
  children?: React.ReactNode;
};

const Value = ({ monthlyIncome, children }: ValueProps) => (
  <BreakdownValue
    name={FIELDS.CALCULATED_MONTHTY_INCOME}
    value={monthlyIncome}
    isCurrency={true}
  >
    {children}
  </BreakdownValue>
);

type OneYearIncomeScenarioProps = {
  monthlyIncome: number;
  currentYear: number;
  currentAnnual: number;
};

const OneYearIncomeScenario = ({
  monthlyIncome,
  currentYear,
  currentAnnual,
}: OneYearIncomeScenarioProps) => (
  <Value monthlyIncome={monthlyIncome}>
    <BreakdownValue
      name={`Annual Income for ${currentYear}`}
      value={currentAnnual}
    />
    <BreakdownValue name="Months" value={<>&divide;12</>} isCurrency={false} />
  </Value>
);

type TwoYearIncomeScenarioProps = {
  monthlyIncome: number;
  currentYear: number;
  priorYear: number;
  currentAnnual: number;
  priorAnnual: number;
};

const TwoYearIncomeScenario = ({
  monthlyIncome,
  currentYear,
  priorYear,
  currentAnnual,
  priorAnnual,
}: TwoYearIncomeScenarioProps) => (
  <Value monthlyIncome={monthlyIncome}>
    <BreakdownValue
      name={`Annual Income for ${currentYear}`}
      value={currentAnnual}
    />
    <BreakdownValue
      name={`Annual Income for ${priorYear}`}
      value={priorAnnual}
    />
    <BreakdownValue name="Months" value={<>&divide;24</>} isCurrency={false} />
  </Value>
);

type MonthlyIncomeValueProps = {
  selectedResult: CalculationResult;
  currentYear: number;
  formulaId?: number;
};

const MonthlyIncomeValue = ({
  selectedResult,
  currentYear,
  formulaId,
}: MonthlyIncomeValueProps) => {
  const priorYear = currentYear - 1;

  const props = {
    monthlyIncome: selectedResult.calculationAmount,
    currentYear,
    priorYear,
    currentAnnual: selectedResult.getInputAsFloat(
      FormTypes.Form1120,
      currentYear,
      'annualIncome',
    ),
    priorAnnual: selectedResult.getInputAsFloat(
      FormTypes.Form1120,
      priorYear,
      'annualIncome',
    ),
  };

  switch (formulaId) {
    case FORMULA1120.OneYearPositiveOrNegativeTend:
    case FORMULA1120.OneYrNegBreakDownFormulaId:
      return <OneYearIncomeScenario {...props} />;

    case FORMULA1120.TwoYearPositiveOrNegativeTrend:
    case FORMULA1120.TwoYrNegBreakdownFormulaId:
      return <TwoYearIncomeScenario {...props} />;

    default:
      return null;
  }
};

type MonthlyIncomeProps = {
  selectedResult: CalculationResult;
  currentYear: number;
  formulaId: number;
};

const MonthlyIncome = (props: Readonly<MonthlyIncomeProps>) => {
  return (
    <BreakdownYearContainer title="Monthly Income Calculation" mb={4}>
      <MonthlyIncomeValue {...props} />
    </BreakdownYearContainer>
  );
};

export default MonthlyIncome;
